import { COMMON_SERVICE_URL, LOCAL_ONLY_COMMON_SERVICE_TOKEN } from "@/src/const";

import { HttpRequest, HttpResponseType, CookieHelper } from "@/src/util";

export type CommonServiceQueryResponseType<T> = {
  data: T[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
};

export type CommonServiceResponseType = {
  get: <T>(id: string, query?: string) => Promise<HttpResponseType<T | undefined>>;
  getAndQueryArray: <T>(filter?: string) => Promise<HttpResponseType<CommonServiceQueryResponseType<T> | undefined>>;
  getAndQuerySingle: <T>(filter?: string) => Promise<HttpResponseType<T | undefined>>;
  post: <T>(body: T) => Promise<HttpResponseType<T | undefined>>;
  put: <T>(id: number, body: T) => Promise<HttpResponseType<T | undefined>>;
  getApiSchemas: (apiId: string) => Promise<HttpResponseType<any>>;
  getAllApiSchemas: () => Promise<HttpResponseType<any>>;
  getApiComponents: () => Promise<HttpResponseType<any>>;
};

export type CommonServiceType = "agent-data" | "common-cms" | "common-data" | "compound-cms" | "relo-cms" | "flowhouse-cms";

interface CommonServiceProps {
  service: CommonServiceType;
  slug?: string;
}

/**
 * This service is used to access the common service API.
 *
 * It will only work with cloudflare authentication or local development and is not intended for use outside toolkit
 *
 * @param service - this is the data instance that you wish to access
 * @param slug - this is the slug of the model in the data instance that you wish to access
 * @returns methods to invoke various data and schema enpoints from common service
 */
function CommonService({ service, slug }: CommonServiceProps): CommonServiceResponseType {
  const authorizationToken = CookieHelper.getCookie("CF_Authorization") || LOCAL_ONLY_COMMON_SERVICE_TOKEN;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authorizationToken}`,
  };

  function get<T>(id: string, query?: string) {
    return HttpRequest.http<T>({
      host: COMMON_SERVICE_URL,
      method: "GET",
      pathname: `v1/data/${service}/${slug}/${id}`,
      headers,
      queryParams: query,
      expectedResponse: {
        json: true,
      },
    });
  }

  async function getAndQueryArray<T>(query?: string) {
    return HttpRequest.http<CommonServiceQueryResponseType<T>>({
      host: COMMON_SERVICE_URL,
      method: "GET",
      pathname: `v1/data/${service}/${slug}`,
      headers,
      queryParams: query,
      expectedResponse: {
        json: true,
      },
    });
  }

  async function getAndQuerySingle<T>(query?: string) {
    return HttpRequest.http<T>({
      host: COMMON_SERVICE_URL,
      method: "GET",
      pathname: `v1/data/${service}/${slug}`,
      headers,
      queryParams: query,
      expectedResponse: {
        json: true,
      },
    });
  }

  async function post<T>(body: T) {
    return await HttpRequest.http<T>({
      host: COMMON_SERVICE_URL,
      method: "POST",
      pathname: `v1/data/${service}/${slug}`,
      headers,
      payload: JSON.stringify(body),
      expectedResponse: {
        json: true,
      },
    });
  }

  async function put<T>(id: number, body: T) {
    return await HttpRequest.http<T>({
      host: COMMON_SERVICE_URL,
      method: "PUT",
      pathname: `v1/data/${service}/${slug}/${id}`,
      headers,
      payload: JSON.stringify(body),
      expectedResponse: {
        json: true,
      },
    });
  }

  async function getAllApiSchemas() {
    return await HttpRequest.http<any>({
      host: COMMON_SERVICE_URL,
      method: "GET",
      pathname: `v1/schema/${service}`,
      headers,
      expectedResponse: {
        json: true,
      },
    });
  }

  async function getApiSchemas(apiId: string) {
    return await HttpRequest.http<any>({
      host: COMMON_SERVICE_URL,
      method: "GET",
      pathname: `v1/schema/${service}/${apiId}`,
      headers,
      expectedResponse: {
        json: true,
      },
    });
  }

  async function getApiComponents() {
    return await HttpRequest.http<any>({
      host: COMMON_SERVICE_URL,
      method: "GET",
      pathname: `v1/components/${service}`,
      headers,
      expectedResponse: {
        json: true,
      },
    });
  }

  return {
    get,
    put,
    getAndQueryArray,
    getAndQuerySingle,
    post,
    getApiSchemas,
    getAllApiSchemas,
    getApiComponents,
  };
}

export default CommonService;
